import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useManualApproveFundTransaction } from '@/hooks/react-query/useFundTransactions';

interface ManualApproveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fundId: string;
  transactionId: string;
}

export function ManualApproveDialog({
  open,
  onOpenChange,
  fundId,
  transactionId,
}: ManualApproveDialogProps) {
  const [reason, setReason] = useState('');
  const manualApprove = useManualApproveFundTransaction(fundId, transactionId);

  async function onSubmit() {
    await manualApprove.mutateAsync({ reason });
    setReason('');
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manually approve</DialogTitle>
          <DialogDescription>
            Skip 2-of-2 verification. A written reason is recorded in the audit
            trail.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="manual-approve-reason">Reason</Label>
          <Textarea
            id="manual-approve-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you overriding the 2-of-2 check?"
            rows={4}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={manualApprove.isPending || reason.trim().length < 10}
            data-testid="submit-manual-approve"
          >
            {manualApprove.isPending ? 'Approving…' : 'Approve with reason'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
